<script setup lang="ts">
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { listEditions } from '@/api/events'
import type { Event, Edition } from '@/types/events'
import SidebarNavItem from '@/components/layout/sidebar/SidebarNavItem.vue'
import SidebarSectionDivider from '@/components/layout/sidebar/SidebarSectionDivider.vue'
import SidebarEventSwitcher from '@/components/layout/sidebar/SidebarEventSwitcher.vue'
import SidebarEditionSwitcher from '@/components/layout/sidebar/SidebarEditionSwitcher.vue'
import SidebarRaceSwitcher from '@/components/layout/sidebar/SidebarRaceSwitcher.vue'

const props = defineProps<{ eventSlug: string; editionSlug: string; raceSlug: string }>()
const emit = defineEmits<{ (e: 'navigate'): void }>()

const { t } = useI18n()
const router = useRouter()

async function handleEventSelect(event: Event): Promise<void> {
  const editions = await listEditions(event.slug)
  const target = editions.find((e) => e.active) ?? editions[0] ?? null
  if (target) {
    await router.push(`/organizer/events/${event.slug}/editions/${target.slug}`)
  } else {
    await router.push(`/organizer/events/${event.slug}`)
  }
}

async function handleEditionSelect(edition: Edition): Promise<void> {
  await router.push(`/organizer/events/${props.eventSlug}/editions/${edition.slug}`)
}

const USERS_ICON = [
  'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
]

const COG_ICON = [
  'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
]

const DOCUMENT_TEXT_ICON = [
  'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
]

const PHOTO_ICON = [
  'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
]

const TROPHY_ICON = [
  'M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0',
]

const MAP_ICON = [
  'M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z',
]
</script>

<template>
  <div class="flex flex-col">
    <SidebarEventSwitcher
      :current-event-slug="props.eventSlug"
      @navigate="emit('navigate')"
      @select="handleEventSelect"
    />
    <SidebarEditionSwitcher
      :current-event-slug="props.eventSlug"
      :current-edition-slug="props.editionSlug"
      @navigate="emit('navigate')"
      @select="handleEditionSelect"
    />
    <SidebarRaceSwitcher
      :current-event-slug="props.eventSlug"
      :current-edition-slug="props.editionSlug"
      :current-race-slug="props.raceSlug"
      @navigate="emit('navigate')"
    />

    <!-- Back link -->
    <div class="px-4 py-3 border-b border-slate-800">
      <RouterLink
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}`"
        class="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        @click="emit('navigate')"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        {{ t('nav.sidebar.back_to_edition') }}
      </RouterLink>
    </div>

    <nav class="flex flex-col py-3" aria-label="Race navigation">
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/participants`"
        :label="t('nav.sidebar.participants')"
        :icon="USERS_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/results`"
        :label="t('nav.sidebar.race_results')"
        :icon="TROPHY_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/edit`"
        :label="t('nav.sidebar.settings')"
        :exact-active="true"
        :icon="COG_ICON"
        @navigate="emit('navigate')"
      />
      <SidebarNavItem
        :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/form`"
        :label="t('nav.sidebar.registration_form')"
        :icon="DOCUMENT_TEXT_ICON"
        @navigate="emit('navigate')"
      />

      <SidebarSectionDivider />

      <div class="px-4 pb-1 pt-2">
        <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {{ t('nav.sidebar.public_website_section') }}
        </p>
      </div>

      <div class="pl-2">
        <SidebarNavItem
          :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/cover-image`"
          :label="t('nav.sidebar.cover_image')"
          :icon="PHOTO_ICON"
          @navigate="emit('navigate')"
        />
        <SidebarNavItem
          :to="`/organizer/events/${props.eventSlug}/editions/${props.editionSlug}/races/${props.raceSlug}/route`"
          :label="t('nav.sidebar.route_map')"
          :icon="MAP_ICON"
          @navigate="emit('navigate')"
        />
      </div>
    </nav>
  </div>
</template>
